import { useEffect, useRef, useState } from "react";
import PricingPanel from "./components/PricingPanel";
import BookingForm from "./components/BookingForm";

const trustItems = [
  { icon: "ri-nurse-line", label: "RN Instructors" },
  { icon: "ri-medal-2-line", label: "Same-Day Cert" },
  { icon: "ri-shield-check-line", label: "AHA-Aligned" },
  { icon: "ri-hand-heart-line", label: "Hands-On" },
  { icon: "ri-map-pin-line", label: "Kissimmee, FL" },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function BookingPage() {
  const [visible, setVisible] = useState(false);
  const [highlighted, setHighlighted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Scroll to form section when landing via #reserve-spot hash
  useEffect(() => {
    if (window.location.hash === '#reserve-spot') {
      const el = document.getElementById('reserve-spot');
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setHighlighted(true);
          setTimeout(() => setHighlighted(false), 2200);
        }, 200);
      }
    }
  }, []);

  return (
    <main className="pt-20" style={{ background: "linear-gradient(160deg, #0d0d1a 0%, #1a0a2e 50%, #0f0f0f 100%)", minHeight: "100vh" }}>
      <title>Book a CPR Class Kissimmee | Same-Day BLS ACLS PALS | Cardiocraft CPR</title>
      <meta name="description" content="Book your CPR certification class in Kissimmee, FL today. Same-day BLS, ACLS, PALS, and Heartsaver CPR/AED slots available. RN instructors, AHA-aligned training. Easy online booking." />
      <meta name="keywords" content="book CPR class Kissimmee, same-day CPR certification, BLS booking Florida, ACLS class Kissimmee schedule" />
      <link rel="canonical" href={`${SITE_URL}/booking`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'CPR Certification Classes – Cardiocraft CPR',
            url: `${SITE_URL}/booking`,
            description: 'Book same-day BLS, ACLS, PALS, and Heartsaver CPR/AED certification classes in Kissimmee, FL. Led by Registered Nurses, AHA-aligned.',
            provider: {
              '@type': 'EducationalOrganization',
              name: 'Cardiocraft CPR',
              address: {
                '@type': 'PostalAddress',
                streetAddress: '1627 East Vine Street, Suite 203',
                addressLocality: 'Kissimmee',
                addressRegion: 'FL',
                postalCode: '34744',
                addressCountry: 'US',
              },
              telephone: '+14075643966',
              email: 'cardiocraftcpr@gmail.com',
            },
            areaServed: {
              '@type': 'City',
              name: 'Kissimmee',
            },
            availableChannel: {
              '@type': 'ServiceChannel',
              serviceUrl: `${SITE_URL}/booking`,
              servicePhone: '+14075643966',
            },
          }),
        }}
      />
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/97f4b4d633cfb0ebdb466c6c14244c19.jpg"
            alt="Book CPR class Kissimmee"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(13,13,26,0.75) 0%, rgba(26,10,46,0.88) 60%, rgba(13,13,26,1) 100%)" }} />
        </div>

        {/* Gold top divider */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-50" />

        <div className="relative z-10 text-center max-w-4xl mx-auto px-6">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
            style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.35)", color: "#D4AF37" }}
          >
            <i className="ri-calendar-check-line" />
            Same-Day Classes Available
          </div>

          <h1 className="font-serif text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Book Your CPR Class{" "}
            <span
              style={{
                background: "linear-gradient(90deg, #D4AF37, #f5d97a, #D4AF37)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              in Minutes
            </span>
          </h1>
          <p className="text-white/65 text-lg max-w-2xl mx-auto leading-relaxed">
            Choose your instructor, pick your time, and get certified the same day with
            RN-led training in Kissimmee, FL.
          </p>
        </div>
      </section>

      {/* Trust strip */}
      <section
        className="py-4 sticky top-20 z-30"
        style={{
          background: "rgba(13,9,26,0.92)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid rgba(212,175,55,0.2)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-center flex-wrap gap-6">
          {trustItems.map((t) => (
            <div key={t.label} className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${t.icon} text-brand-gold text-base`} />
              </div>
              <span className="text-white/65 text-xs font-semibold whitespace-nowrap">{t.label}</span>
            </div>
          ))}
          {/* Quick call CTAs */}
          <div className="flex items-center gap-3 ml-4 pl-4" style={{ borderLeft: "1px solid rgba(255,255,255,0.1)" }}>
            <a
              href="tel:4075643966"
              className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-all duration-200 hover:opacity-80"
              style={{ background: "rgba(107,70,193,0.3)", border: "1px solid rgba(107,70,193,0.5)", color: "#D4AF37" }}
            >
              <i className="ri-phone-line" /> 407-564-3966
            </a>
            <a
              href="tel:9172856715"
              className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-all duration-200 hover:opacity-80"
              style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)", color: "#D4AF37" }}
            >
              <i className="ri-phone-line" /> 917-285-6715
            </a>
          </div>
        </div>
      </section>

      {/* Main 2-col layout */}
      <section
        id="reserve-spot"
        className={`py-16 ${highlighted ? 'reserve-highlight' : ''}`}
        style={{ scrollMarginTop: '88px' }}
      >
        <div
          ref={ref}
          className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-[1fr_520px] gap-10 items-start transition-all duration-700"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(30px)",
          }}
        >
          {/* LEFT: Pricing Panel */}
          <div>
            {/* Section label */}
            <div className="flex items-center gap-3 mb-6">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest"
                style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.3)", color: "#D4AF37" }}
              >
                <i className="ri-price-tag-3-line" />
                Pricing
              </div>
              <p className="text-white/40 text-sm">All courses led by Registered Nurses</p>
            </div>

            <PricingPanel />
          </div>

          {/* RIGHT: Booking Form */}
          <div className="lg:sticky lg:top-36">
            <div className="flex items-center gap-3 mb-6">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest"
                style={{ background: "rgba(107,70,193,0.15)", border: "1px solid rgba(107,70,193,0.35)", color: "#c4b5fd" }}
              >
                <i className="ri-calendar-2-line" />
                Book Now
              </div>
              <p className="text-white/40 text-sm">Same-day slots available</p>
            </div>

            <BookingForm />

            {/* Text-to-book note */}
            <div
              className="mt-4 px-5 py-4 rounded-xl flex items-center gap-3"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <i className="ri-message-3-line text-brand-gold text-lg shrink-0" />
              <p className="text-white/50 text-xs leading-relaxed">
                Prefer to text? Send your name, course, and date to{" "}
                <a href="sms:4075643966" className="text-brand-gold font-bold">407-564-3966</a>
                {" "}and we&apos;ll confirm your spot.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA banner */}
      <section
        className="py-14 relative overflow-hidden"
        style={{ borderTop: "1px solid rgba(212,175,55,0.15)" }}
      >
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at center, rgba(107,70,193,0.1) 0%, transparent 70%)" }}
        />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <p className="text-brand-gold text-xs font-bold uppercase tracking-widest mb-3">Have Questions?</p>
          <h3 className="font-serif text-3xl font-bold text-white mb-4">
            Not sure which course is right for you?
          </h3>
          <p className="text-white/55 mb-8 leading-relaxed">
            Our RN instructors are happy to help you find the perfect certification path.
            Call, text, or use our contact form.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:4075643966"
              className="flex items-center gap-2 font-bold text-sm px-7 py-3.5 rounded-lg cursor-pointer whitespace-nowrap transition-all duration-300 hover:scale-105"
              style={{ background: "linear-gradient(135deg, #D4AF37, #b8952e)", color: "#1A1A1A", boxShadow: "0 4px 20px rgba(212,175,55,0.35)" }}
            >
              <i className="ri-phone-fill" /> Call to Book
            </a>
            <a
              href="/contact"
              className="flex items-center gap-2 font-semibold text-sm px-7 py-3.5 rounded-lg cursor-pointer whitespace-nowrap transition-all duration-300 hover:bg-white/10"
              style={{ border: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.8)" }}
            >
              Contact Us
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
